import { createFileRoute, Navigate, redirect } from "@tanstack/react-router";

import { appInstanceApi } from "@workspace/app-instance/api";
import { getCurrentTenant, useTenant } from "@workspace/tenant-context";

import { firstLocationId } from "@/lib/first-location";
import { NoLocations } from "@/components/no-locations";
import { PageLoadingFull } from "@/components/page-loading";

export const Route = createFileRoute("/")({
	beforeLoad: async ({ context, location }) => {
		if (!context.auth.isAuthenticated) {
			throw redirect({
				to: "/login",
				search: {
					redirect: location.href,
				},
			});
		}

		// HA-746: await the app instance rather than read `context.tenants.root`, a snapshot that
		// can be taken before the tenant query resolves; that left `/` on "Loading..." with nothing
		// to redirect it later. When no tenant is known yet (first login, nothing stored), the
		// component below redirects once the live tenant data has one.
		const tenant = getCurrentTenant();
		if (!tenant) {
			return;
		}

		const app = await context.queryClient
			.ensureQueryData(appInstanceApi.getOptionsCurrent(tenant))
			.then((d) => d.item)
			.catch(() => null);

		// HA-753: no locations means nowhere to redirect; the component says so.
		const venueId = firstLocationId(app);
		if (!app || !venueId) {
			return;
		}

		throw redirect({
			to: "/$tenant/venues/$venueId",
			params: {
				tenant: app.id,
				venueId,
			},
		});
	},
	component: App,
	pendingComponent: PageLoadingFull,
});

function App() {
	const { root } = useTenant();
	const app = root?.item;

	if (app) {
		// HA-753: `locations[0]!` threw here for a tenant with no locations.
		const venueId = firstLocationId(app);
		if (!venueId) return <NoLocations />;

		return (
			<Navigate
				to="/$tenant/venues/$venueId"
				params={{ tenant: app.id, venueId }}
				replace
			/>
		);
	}

	return <PageLoadingFull />;
}
